import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { fmtNum } from "@/lib/format";
import ProductThumb from "@/components/ProductThumb";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Loader2, Ship, PackageCheck, Globe } from "lucide-react";

const REGION_COLORS = { US: "#007AFF", UK: "#10B981", EU: "#F59E0B", Other: "#8B5CF6" };

export default function WarehouseAnalyticsDrawer({ sku, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sku) return;
    setLoading(true);
    setData(null);
    api.get(`/warehouse/${encodeURIComponent(sku)}/analytics`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [sku]);

  const regionRows = data ? Object.entries(data.sent.region_totals).filter(([, v]) => v > 0).map(([region, units]) => ({ region, units })) : [];

  return (
    <Sheet open={!!sku} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="bg-[#0a0a0a] border-l border-[#1e1e1e] text-white w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="warehouse-analytics-drawer">
        {loading || !data ? (
          <div className="flex items-center justify-center h-64 text-[#666]">
            <SheetHeader className="sr-only"><SheetTitle>Warehouse analytics</SheetTitle><SheetDescription>Loading analytics</SheetDescription></SheetHeader>
            {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : "No data"}
          </div>
        ) : (
          <div>
            <SheetHeader className="p-5 border-b border-[#1a1a1a] space-y-0">
              <div className="flex items-center gap-4">
                <ProductThumb src={data.image} size={56} title={data.name} />
                <div className="min-w-0">
                  <SheetTitle className="text-white font-head text-lg truncate">{data.name || data.warehouse_sku}</SheetTitle>
                  <SheetDescription className="text-[#A3A3A3] tnum text-xs">{data.warehouse_sku} · {data.linked_skus.length} linked SKU(s)</SheetDescription>
                </div>
              </div>
            </SheetHeader>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-3" data-testid="wa-kpis">
                <div className="rounded-xl bg-[#0f0f0f] border border-[#1a1a1a] p-3">
                  <p className="text-[10px] uppercase tracking-widest text-[#666] flex items-center gap-1"><Ship className="h-3 w-3" /> Sent to Amazon</p>
                  <p className="text-xl font-bold text-[#007AFF] tnum">{fmtNum(data.sent.total)}</p>
                </div>
                <div className="rounded-xl bg-[#0f0f0f] border border-[#1a1a1a] p-3">
                  <p className="text-[10px] uppercase tracking-widest text-[#666] flex items-center gap-1"><PackageCheck className="h-3 w-3" /> Received from Suppliers</p>
                  <p className="text-xl font-bold text-[#10B981] tnum">{fmtNum(data.received.total)}</p>
                </div>
              </div>

              {/* Sent to Amazon by region over time */}
              <div className="rounded-xl bg-[#0f0f0f] border border-[#1a1a1a] p-4" data-testid="wa-sent-chart">
                <div className="flex items-center gap-2 mb-1"><Globe className="h-4 w-4 text-[#007AFF]" /><h4 className="font-head font-bold text-white text-sm">Sent to Amazon by Region</h4></div>
                <p className="text-xs text-[#666] mb-3">Units dispatched per month · US / UK / EU</p>
                {data.sent.timeline.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.sent.timeline} margin={{ left: -12, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
                      <XAxis dataKey="period" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#1a1a1a" }} />
                      <YAxis tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#0d0d0d", border: "1px solid #262626", borderRadius: 8, color: "#fff" }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="US" stackId="a" fill={REGION_COLORS.US} />
                      <Bar dataKey="UK" stackId="a" fill={REGION_COLORS.UK} />
                      <Bar dataKey="EU" stackId="a" fill={REGION_COLORS.EU} />
                      <Bar dataKey="Other" stackId="a" fill={REGION_COLORS.Other} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-[#666] text-xs py-6 text-center">No FBA shipments recorded for this SKU yet.</p>}
                {regionRows.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-3">
                    {regionRows.map((r) => (
                      <span key={r.region} className="flex items-center gap-1.5 text-xs">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: REGION_COLORS[r.region] }} />
                        <span className="text-[#A3A3A3]">{r.region}</span>
                        <span className="tnum text-white font-semibold">{fmtNum(r.units)}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Received from suppliers over time */}
              <div className="rounded-xl bg-[#0f0f0f] border border-[#1a1a1a] p-4" data-testid="wa-received-chart">
                <div className="flex items-center gap-2 mb-1"><PackageCheck className="h-4 w-4 text-[#10B981]" /><h4 className="font-head font-bold text-white text-sm">Received into Warehouse</h4></div>
                <p className="text-xs text-[#666] mb-3">Units received from suppliers (via Purchase Orders) per month</p>
                {data.received.timeline.length ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.received.timeline} margin={{ left: -12, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
                      <XAxis dataKey="period" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#1a1a1a" }} />
                      <YAxis tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#0d0d0d", border: "1px solid #262626", borderRadius: 8, color: "#fff" }} />
                      <Bar dataKey="received" name="Received" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-[#666] text-xs py-6 text-center">No supplier receipts yet — receive a Purchase Order to see this.</p>}
              </div>

              {/* Recent receipts */}
              {data.received.recent.length > 0 && (
                <div className="rounded-xl bg-[#0f0f0f] border border-[#1a1a1a] overflow-hidden" data-testid="wa-recent-receipts">
                  <div className="px-4 py-3 border-b border-[#1a1a1a]"><h4 className="font-head font-bold text-white text-sm">Recent Receipts</h4></div>
                  <div className="divide-y divide-[#141414]">
                    {data.received.recent.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                        <span className="text-[#A3A3A3] tnum text-xs">{r.date}</span>
                        <span className="flex items-center gap-3 tnum text-xs">
                          <span className="text-[#666]">{r.po_number || "—"}</span>
                          <span className="text-[#10B981] font-semibold">+{fmtNum(r.quantity)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
